// frontend/src/components/legal/termsOfService.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { getPublicPageDefinition } from '../public/publicSiteRegistry.mjs';
import {
  LegalCallout,
  LegalContactBlock,
  LegalList,
  LegalPageShell,
  LegalSection,
} from './legalPageShell';

const PAGE = getPublicPageDefinition('/terms-of-service');
const EFFECTIVE_DATE = 'November 3, 2026';

const TABLE_OF_CONTENTS = [
  ['terms-platform-services', '1. Platform and field services'],
  ['terms-accounts', '2. Accounts, roles, and authority'],
  ['terms-content', '3. Operational information and user content'],
  ['terms-google-drive', '4. Google Drive receipt storage'],
  ['terms-field-services', '5. Field-service requests, scheduling, and estimates'],
  ['terms-fees', '6. Fees, invoices, and payment'],
  ['terms-acceptable-use', '7. Acceptable use'],
  ['terms-third-parties', '8. Third-party services'],
  ['terms-ip', '9. TaskerSys intellectual property'],
  ['terms-privacy', '10. Privacy'],
  ['terms-availability', '11. Service availability and changes'],
  ['terms-emergency', '12. No emergency service'],
  ['terms-disclaimers', '13. Disclaimers'],
  ['terms-liability', '14. Limitation of liability'],
  ['terms-indemnification', '15. Indemnification'],
  ['terms-termination', '16. Suspension and termination'],
  ['terms-law', '17. Governing law'],
  ['terms-changes', '18. Changes to these Terms'],
  ['terms-contact', '19. Contact'],
].map(([id, label]) => ({ id, label }));

export default function TermsOfService() {
  return (
    <LegalPageShell
      page={PAGE}
      effectiveDate={EFFECTIVE_DATE}
      tableOfContents={TABLE_OF_CONTENTS}
      summary={
        <p>
          These Terms of Service (&quot;Terms&quot;) govern access to and use of TaskerSys,
          taskersys.com, the TaskerSys property-operations platform, and related services
          offered by Copala Inc. (&quot;TaskerSys,&quot; &quot;Copala,&quot; &quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;). By accessing or using TaskerSys, creating an
          account, connecting a third-party service, or ordering services, you agree to these
          Terms.
        </p>
      }
      closingNote={
        <p>
          These Terms are a general website and platform agreement. Project-specific service
          agreements, proposals, purchase orders, statements of work, change orders, and other
          accepted commercial documents may contain additional or controlling terms.
        </p>
      }
    >
      <LegalSection id="terms-platform-services" title="1. TaskerSys platform and field services">
        <p>
          TaskerSys supports two ways of working with us: users may use the TaskerSys platform to
          coordinate property operations, maintenance, turnovers, vendors, approvals, reporting,
          and related workflows, and users may separately engage TaskerSys or its service teams for
          cleaning, repairs, maintenance, light renovation, hotel renovation, or other field
          services.
        </p>
        <p>
          Software access and field services are distinct offerings. A separate quote, proposal,
          service agreement, statement of work, purchase order, change order, or other written
          agreement may apply to a specific field-service engagement. If a signed or accepted
          project-specific agreement conflicts with these Terms, the project-specific agreement
          controls for that engagement.
        </p>
      </LegalSection>

      <LegalSection id="terms-accounts" title="2. Accounts, roles, and authority">
        <p>
          TaskerSys may provide different workspaces, permissions, and capabilities for property
          owners, property managers, general managers, managers, service providers, dispatchers,
          taskers, subcontractors, technicians, implementers, administrators, and other
          participants.
        </p>
        <p>
          You are responsible for providing accurate account information, protecting your login
          credentials, and all activity under your account. You may only create, assign, approve,
          upload, or disclose information if you have authority to do so for the relevant
          organization, property, project, person, document, or transaction.
        </p>
        <p>
          Organizations are responsible for determining which users should have access to their
          TaskerSys data and for promptly updating permissions when roles change.
        </p>
      </LegalSection>

      <LegalSection id="terms-content" title="3. Operational information and user content">
        <p>
          You may submit service requests, tickets, scopes, approvals, comments, messages, photos,
          videos, receipts, invoices, bids, quotes, reports, payment information, scheduling
          details, and other content (&quot;User Content&quot;).
        </p>
        <p>
          You retain ownership of your User Content. You grant TaskerSys a limited, non-exclusive
          license to host, process, copy, display, transmit, and otherwise use User Content as
          necessary to provide the TaskerSys features and services you request, maintain records,
          protect the platform, and comply with law.
        </p>
        <p>
          You are responsible for ensuring User Content is lawful, accurate when accuracy is
          important to the workflow, and appropriate to share with the intended TaskerSys users or
          recipients.
        </p>
      </LegalSection>

      <LegalSection id="terms-google-drive" title="4. Google Drive receipt storage">
        <p>
          TaskerSys may allow a user to connect a Google account and select a Google Drive folder
          for invoice receipt storage. This feature is optional.
        </p>
        <p>
          When enabled, TaskerSys may create invoice and receipt folders, upload or synchronize
          receipt documents, and create a view-only receipt-folder link for inclusion on an invoice.
          TaskerSys uses the Google Drive <code>drive.file</code> permission so access is limited
          to files and folders the user chooses to use with TaskerSys or that TaskerSys creates
          through the feature.
        </p>
        <p>
          If you enable an &quot;anyone with the link&quot; receipt folder, anyone who receives or
          obtains that link may be able to view or download the files in that folder. You are
          responsible for deciding whether the documents are appropriate for link-based sharing and
          for managing or removing Google Drive permissions when access is no longer desired.
        </p>
        <p>
          Connecting Google Drive does not transfer ownership of your Google Drive to TaskerSys.
          Files stored in your Google Drive remain subject to your Google account and Google&apos;s
          terms. Google may change, restrict, suspend, or discontinue APIs or permissions, and a
          Google Workspace administrator may restrict third-party application access.
        </p>
      </LegalSection>

      <LegalSection id="terms-field-services" title="5. Field-service requests, scheduling, and estimates">
        <p>
          Website descriptions of response times, scheduling, pricing, availability, budget
          references, or service categories are general information and are not guaranteed unless
          confirmed in a specific written agreement. Availability may depend on geography,
          staffing, access, building rules, materials, scope, weather, client approvals, and other
          conditions.
        </p>
        <p>
          You are responsible for providing safe and lawful access to the work area, disclosing
          known conditions that may affect the work, and obtaining building, owner, tenant,
          governmental, or other approvals that are your responsibility under the applicable
          project agreement.
        </p>
        <p>
          Additional work, concealed conditions, access limitations, client-directed changes, and
          scope changes may require a revised price, schedule, or change order.
        </p>
      </LegalSection>

      <LegalSection id="terms-fees" title="6. Fees, invoices, and payment">
        <p>
          Fees for software subscriptions, field services, or other paid features are those
          displayed at purchase or set out in the applicable proposal, invoice, agreement,
          subscription, or order. You agree to pay valid charges according to the stated payment
          terms.
        </p>
        <p>
          TaskerSys may provide operational, invoice, receipt, payable, receivable,
          reconciliation, or accounting-related tools. These tools assist with business records and
          workflow management; they are not a substitute for professional accounting, tax, legal,
          or financial advice.
        </p>
      </LegalSection>

      <LegalSection id="terms-acceptable-use" title="7. Acceptable use">
        <p>You agree not to:</p>
        <LegalList>
          <li>use TaskerSys for unlawful, fraudulent, deceptive, or abusive activity;</li>
          <li>access another user&apos;s account or data without authorization;</li>
          <li>
            upload malware, malicious code, unlawful material, or content that infringes
            third-party rights;
          </li>
          <li>
            interfere with the security, availability, integrity, or performance of TaskerSys or
            connected services;
          </li>
          <li>
            scrape, probe, reverse engineer, or attempt to bypass security or access controls
            except where expressly permitted by law;
          </li>
          <li>use TaskerSys to send spam or unauthorized commercial communications; or</li>
          <li>
            use Google user data obtained through TaskerSys for a purpose outside the user-facing
            Google Drive features for which access was granted.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection id="terms-third-parties" title="8. Third-party services">
        <p>
          TaskerSys may integrate with third-party services such as Google Drive, cloud-storage
          providers, payment or accounting tools, communications services, and other systems.
          Third-party services are governed by their own terms, privacy policies, availability, and
          security practices.
        </p>
        <p>
          We are not responsible for outages, changes, restrictions, or acts of a third-party
          service that are outside TaskerSys&apos;s reasonable control.
        </p>
      </LegalSection>

      <LegalSection id="terms-ip" title="9. TaskerSys intellectual property">
        <p>
          TaskerSys, its software, site design, workflow logic, branding, text, interfaces, and
          related intellectual property are owned by Copala Inc. or its licensors, except for User
          Content and third-party materials.
        </p>
        <p>
          Subject to these Terms and any applicable subscription or service agreement, we grant you
          a limited, revocable, non-exclusive, non-transferable right to use TaskerSys for your
          internal business and authorized operational purposes.
        </p>
      </LegalSection>

      <LegalSection id="terms-privacy" title="10. Privacy">
        <p>
          Our collection and use of personal information is described in the TaskerSys{' '}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection id="terms-availability" title="11. Service availability and changes">
        <p>
          We may modify, improve, add, remove, or discontinue features from time to time. We may
          perform maintenance or suspend access where reasonably necessary for security, legal
          compliance, system integrity, or operational reasons.
        </p>
        <p>We do not promise that TaskerSys or any third-party integration will be uninterrupted or error-free.</p>
      </LegalSection>

      <LegalSection id="terms-emergency" title="12. No emergency service">
        <LegalCallout tone="warning">
          <strong>TaskerSys is not an emergency-response service.</strong> Do not rely on TaskerSys
          for police, fire, medical, gas-leak, life-safety, or other emergency response. Contact the
          appropriate emergency service or building authority where an immediate safety risk exists.
        </LegalCallout>
      </LegalSection>

      <LegalSection id="terms-disclaimers" title="13. Disclaimers">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE TASKERSYS PLATFORM AND GENERAL WEBSITE CONTENT
          ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; WE DISCLAIM IMPLIED WARRANTIES
          OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT, EXCEPT TO THE
          EXTENT A WARRANTY CANNOT LEGALLY BE DISCLAIMED.
        </p>
        <p>
          Specific field services may carry warranties or obligations stated in the applicable
          written project agreement. Those project-specific commitments are not waived by this
          general platform disclaimer.
        </p>
      </LegalSection>

      <LegalSection id="terms-liability" title="14. Limitation of liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, COPALA INC. AND TASKERSYS WILL NOT BE LIABLE FOR
          INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES ARISING FROM
          USE OF THE PLATFORM OR A THIRD-PARTY INTEGRATION, INCLUDING LOST PROFITS, LOST DATA, OR
          BUSINESS INTERRUPTION, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          Nothing in these Terms limits liability that cannot lawfully be limited. Liability
          relating to a specific field-service project may also be governed by the applicable
          written service agreement, proposal, or contract.
        </p>
      </LegalSection>

      <LegalSection id="terms-indemnification" title="15. Indemnification">
        <p>
          To the extent permitted by law, you agree to indemnify and hold Copala Inc. harmless from
          third-party claims arising from your unlawful use of TaskerSys, your violation of these
          Terms, or User Content you submit without the necessary rights or authority. This section
          does not apply to the extent a claim results from Copala Inc.&apos;s own misconduct or
          where prohibited by law.
        </p>
      </LegalSection>

      <LegalSection id="terms-termination" title="16. Suspension and termination">
        <p>
          You may stop using TaskerSys at any time. We may suspend or terminate access where
          reasonably necessary for nonpayment, security, abuse, legal compliance, material breach,
          or protection of other users or the service.
        </p>
        <p>
          Ending TaskerSys access does not automatically cancel payment obligations, accepted
          field-service work, or record-retention obligations that arose before termination.
        </p>
      </LegalSection>

      <LegalSection id="terms-law" title="17. Governing law">
        <p>
          Unless a separate written agreement states otherwise, these Terms are governed by the
          laws of the State of New York, without regard to conflict-of-law principles. Any mandatory
          rights available to you under applicable law remain unaffected.
        </p>
      </LegalSection>

      <LegalSection id="terms-changes" title="18. Changes to these Terms">
        <p>
          We may update these Terms from time to time. We will update the effective date above when
          changes are made. Material changes may be communicated through the platform or other
          reasonable notice where appropriate. Continued use after the effective date of updated
          Terms constitutes acceptance where permitted by law.
        </p>
      </LegalSection>

      <LegalSection id="terms-contact" title="19. Contact">
        <LegalContactBlock />
      </LegalSection>
    </LegalPageShell>
  );
}


